import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowLeft } from 'lucide-react'
import { DataTable } from '@/components/data-table'
import { PageHeader } from '@/components/page'
import { ProviderIcon } from '@/components/provider-icon'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardTitle } from '@/components/ui/card'
import { localStamp } from '@/lib/datetime'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useUser,
  useUserLedger,
  type LedgerEntry,
  type MemberQuest,
  type MemberQuestState,
  type MemberTaskState,
  type UserDetail,
  type UserRoleCode,
} from '@/features/users/api'

type BadgeVariant = 'default' | 'accent' | 'success' | 'warning' | 'destructive'

const QUEST_STATE: Record<MemberQuestState, { label: string; variant: BadgeVariant }> = {
  in_progress: { label: 'In progress', variant: 'accent' },
  awaiting_reward: { label: 'Awaiting reward', variant: 'warning' },
  rewarded: { label: 'Rewarded', variant: 'success' },
  reward_rejected: { label: 'Reward rejected', variant: 'destructive' },
}

const TASK_STATE: Record<MemberTaskState, { label: string; variant: BadgeVariant }> = {
  pending: { label: 'Not done', variant: 'default' },
  submitted: { label: 'Awaiting verification', variant: 'warning' },
  verified: { label: 'Verified', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'destructive' },
}

const ROLE_LABEL: Record<UserRoleCode, string> = {
  member: 'Member',
  ops_admin: 'Operations Admin',
  super_admin: 'Super Admin',
}

const LEDGER_REASON: Record<LedgerEntry['reason'], string> = {
  quest_reward: 'Quest reward',
  manual_adjustment: 'Manual adjustment',
  shop_purchase: 'Shop purchase',
}

/** Local, with the zone spelled out — see localStamp. */
function stamp(iso: string): string {
  return localStamp(iso)
}

const LEDGER_PAGE_SIZE = 20

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [ledgerPage, setLedgerPage] = useState(1)

  const query = useUser(id)
  // A real second query against its own route, so DataTable's server-side
  // pagination works exactly as it does on every other list.
  const ledgerQuery = useUserLedger(id, { page: ledgerPage, pageSize: LEDGER_PAGE_SIZE })

  const ledgerColumns = useMemo<ColumnDef<LedgerEntry, unknown>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'When',
        cell: ({ row }) => <span className="text-sm">{stamp(row.original.created_at)}</span>,
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        cell: ({ row }) => {
          // Signed on the wire: a debit is a negative row, never a separate
          // column or a flag, because the balance is SUM(amount).
          const credit = row.original.amount >= 0
          return (
            <span
              className={`text-sm font-semibold ${credit ? 'text-[#0f8f5f]' : 'text-destructive'}`}
            >
              {credit ? '+' : '−'}
              {Math.abs(row.original.amount).toLocaleString()}
            </span>
          )
        },
      },
      {
        accessorKey: 'reason',
        header: 'Reason',
        cell: ({ row }) => (
          <span className="text-sm">{LEDGER_REASON[row.original.reason] ?? row.original.reason}</span>
        ),
      },
      {
        id: 'ref',
        header: 'Reference',
        enableSorting: false,
        cell: ({ row }) => {
          const { ref_type, ref_id } = row.original
          if (!ref_type || ref_id === null) return <span className="text-faint">—</span>
          // Quest rewards point back at the quest that paid them, which is the
          // one reference an operator actually follows.
          return ref_type === 'quest' ? (
            <Link to="/quests" className="text-sm text-primary hover:underline">
              Quest #{ref_id}
            </Link>
          ) : (
            <span className="text-sm text-muted-foreground">
              {ref_type} #{ref_id}
            </span>
          )
        },
      },
    ],
    [],
  )

  if (query.isPending) {
    return (
      <>
        <PageHeader title="User" />
        <div className="space-y-4">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </>
    )
  }

  if (query.isError) {
    return (
      <>
        <PageHeader title="User" />
        <Card>
          <CardTitle>Could not load this user</CardTitle>
          <p className="mt-2 text-sm text-muted-foreground">{(query.error as Error).message}</p>
          <div className="mt-4 flex gap-2">
            <Button variant="secondary" onClick={() => void query.refetch()}>
              Try again
            </Button>
            <Button variant="ghost" asChild>
              <Link to="/users">Back to users</Link>
            </Button>
          </div>
        </Card>
      </>
    )
  }

  const user = query.data as UserDetail

  return (
    <>
      <PageHeader
        title={user.display_name}
        actions={
          <Button variant="secondary" asChild>
            <Link to="/users">
              <ArrowLeft />
              Back to users
            </Link>
          </Button>
        }
      />

      <div className="space-y-4">
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{user.display_name}</span>
                {/* Role is derived from staff_user_roles, not a users column — so it
                    is shown here as context, never as an editable field. */}
                <Badge variant={user.role === 'member' ? 'default' : 'accent'}>
                  {ROLE_LABEL[user.role] ?? user.role}
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground">{user.email || '—'}</div>
              <code className="inline-block rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                {user.id}
              </code>
            </div>
            <div className="text-right text-sm">
              <div className="text-faint">Joined</div>
              <div>{stamp(user.joined_at)}</div>
            </div>
          </div>
        </Card>

        <div className="grid gap-4 lg:grid-cols-3">
          <Card>
            <CardTitle>Linked accounts</CardTitle>
            {user.linked_accounts.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">No linked providers.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {user.linked_accounts.map((account) => (
                  <li
                    key={`${account.provider}:${account.email}`}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <ProviderIcon provider={account.provider} />
                    <span className="text-muted-foreground">{account.email || '—'}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-3 text-xs text-faint">
              From <code>auth_identities</code>. <code>users</code> has no email column, so the
              address above comes from here.
            </p>
          </Card>

          <Card>
            <CardTitle>Biconomy binding</CardTitle>
            {user.biconomy?.bound ? (
              <div className="mt-3 space-y-1.5">
                <Badge variant="success">Bound</Badge>
                <code className="block rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                  {user.biconomy.uid}
                </code>
              </div>
            ) : (
              <div className="mt-3">
                <Badge variant="default">Not bound</Badge>
              </div>
            )}
            <p className="mt-3 text-xs text-faint">
              <b>Uniqueness only — no staff verification in Phase 1.</b> One UID per member,
              first come first served, so a binding never reaches the verification queue.
            </p>
          </Card>

          <Card>
            <CardTitle>X (Twitter) handle</CardTitle>
            {user.twitter?.bound ? (
              <div className="mt-3 space-y-1.5">
                <Badge variant="success">Bound</Badge>
                <code className="block rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                  @{user.twitter.handle}
                </code>
              </div>
            ) : (
              <div className="mt-3">
                <Badge variant="default">Not bound</Badge>
              </div>
            )}
            <p className="mt-3 text-xs text-faint">
              <b>Manually typed, not OAuth.</b> Uniqueness only — no follow or verification
              check in Phase 1, same as Biconomy.
            </p>
          </Card>
        </div>

        <Card>
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <CardTitle>Points</CardTitle>
            <div className="text-right">
              <span className="text-2xl font-bold">{user.points.balance.toLocaleString()}</span>
              <span className="ml-1 text-sm text-muted-foreground">points</span>
            </div>
          </div>
          <p className="mt-1 mb-3 text-xs text-faint">
            The balance is <code>SUM(amount)</code> over every ledger row and is never stored, so
            the two cannot disagree. The ledger is append-only — a correction is another row, not
            an edit — and it is paged from its own endpoint, because unlike quest progress it
            grows for the life of the account.
          </p>
          <DataTable
            columns={ledgerColumns}
            query={ledgerQuery}
            pagination={{ page: ledgerPage, onPageChange: setLedgerPage }}
            emptyMessage="No points activity yet."
          />
        </Card>

        <Card>
          <CardTitle>Quest progress</CardTitle>
          <p className="mt-1 text-xs text-faint">
            Only quests this member has acted on appear here. Progress rows are created on the
            first qualifying action, so publishing a quest writes nothing and an untouched quest
            is simply absent — not started, rather than missing.
          </p>
          {user.quests.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No quest activity yet.</p>
          ) : (
            <div className="mt-3 space-y-3">
              {user.quests.map((quest) => (
                <QuestProgress key={quest.quest_id} quest={quest} />
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  )
}

function QuestProgress({ quest }: { quest: MemberQuest }) {
  const state = QUEST_STATE[quest.state]
  const done = quest.tasks.filter((item) => item.state === 'verified').length

  return (
    <div className="rounded-lg border border-input p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{quest.name}</span>
          <Badge variant={state?.variant ?? 'default'}>{state?.label ?? quest.state}</Badge>
        </div>
        <span className="text-sm text-muted-foreground">
          {done}/{quest.tasks.length} verified
          {quest.completed_at && <> · completed {stamp(quest.completed_at)}</>}
        </span>
      </div>

      <ul className="mt-2.5 space-y-2">
        {quest.tasks.map((item) => {
          const taskState = TASK_STATE[item.state]
          // Requested but not yet in the contract (Q21) — rendered only when the
          // backend actually sends it, so the narrow shape degrades quietly.
          const decided = item.verified_by || item.verified_at || item.note
          return (
            <li key={item.task_id} className="text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>{item.title}</span>
                <Badge variant={taskState?.variant ?? 'default'}>
                  {taskState?.label ?? item.state}
                </Badge>
              </div>
              {decided && (
                <div className="mt-0.5 text-xs text-faint">
                  {item.verified_by && <>by {item.verified_by}</>}
                  {item.verified_at && <> · {stamp(item.verified_at)}</>}
                  {item.note && <div className="mt-0.5 italic">“{item.note}”</div>}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
